import React, { useEffect, useState } from "react";
import { Modal, Box, Typography, IconButton, CircularProgress } from "@mui/material";
import CloseIcon from '@mui/icons-material/Close';
import CampaignJob from "../../../interfaces/campaignJob";
import CampaignService from "../../../services/campaignJobService";
import GeneratedDocumentsResponse from "../../../interfaces/generatedDocumentsResponse";
import SearchResultGrid from "../../searchResultGrid/searchResultGrid";
import getGeneratedDocumentColumnDef from "../../../colDef/generatedDocumentColumnDef";
import useIsMounted from "../../../utils/useMountedRef";

interface GeneratedDocumentsModalProps {
    open: boolean;
    onClose: () => void;
    currentCampaignJob: CampaignJob;
};

const styles = {
    modalStyle: {
        position: 'absolute' as 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        width: '90%',
        height: '80%',
        bgcolor: 'background.paper',
        borderRadius: 4,
        boxShadow: 24,
        p: 4,
        overflowY: 'auto'
    },
    modalBox: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
    },
    loaderBox: {
        width: "100%",
        height: "100%",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
    },
    pdfBox: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        overflowY: 'auto',
        height: '90%',
        mt: 1
    },
    centralTextBox: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '90%',
        mt: 1
    },
    grid: {
        mt: 2
    },
    boldText: {
        fontWeight: 'bold'
    },
};

const GeneratedDocumentsModal: React.FC<GeneratedDocumentsModalProps> = ({ open, onClose, currentCampaignJob }) => {
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [generatedDocuments, setGeneratedDocuments] = useState<GeneratedDocumentsResponse[]>([]);
    const [generatedDocumentLink, setGeneratedDocumentLink] = useState<string>('');
    const isMountedRef = useIsMounted();

    const finalColumnDef = getGeneratedDocumentColumnDef(setGeneratedDocumentLink);

    const fetchGeneratedDocuments = async () => {
        try {
            setIsLoading(true);
            const response = await CampaignService.getCampaignJobResults(currentCampaignJob.campaign_type, currentCampaignJob.campaign_job_id);
            if (!isMountedRef.current) return;
            setGeneratedDocuments(response);
        } catch (_) {} finally {
            if (isMountedRef.current) {
                setIsLoading(false);
            }
        }
    };

    useEffect(() => {
        fetchGeneratedDocuments();
    }, []);

    return (
        <Modal open={open} onClose={onClose}>
            <Box sx={styles.modalStyle}>
                <Box sx={styles.modalBox}>
                    <Typography id="modal-title" variant="body1">
                        Generated documents for Campaign Name:
                        <Typography sx={styles.boldText} component="span"> {currentCampaignJob.campaign_name} </Typography>
                        & Job ID:
                        <Typography sx={styles.boldText} component="span"> {currentCampaignJob.campaign_job_id}</Typography>
                    </Typography>
                    <IconButton onClick={onClose}>
                        <CloseIcon />
                    </IconButton>
                </Box>
                {isLoading ? (
                    <Box sx={styles.loaderBox}>
                        <CircularProgress color="primary" />
                    </Box>
                ) : generatedDocuments.length > 0 ? (
                    <>
                        {generatedDocumentLink ? (
                            <Box sx={styles.pdfBox}>
                                <embed
                                    src={generatedDocumentLink}
                                    type="application/pdf"
                                    width="100%"
                                    height="100%"
                                />
                            </Box>
                        ) : (
                            <Box sx={styles.grid}>
                                <SearchResultGrid
                                    results={generatedDocuments}
                                    columns={finalColumnDef}
                                    columnVisibilityModel={{ signed_url: false }}
                                    getRowId={(val: GeneratedDocumentsResponse) => val.id}
                                    rowCount={generatedDocuments.length}
                                    onSelectRow={() => {}}
                                    height="75vh"
                                    includeHiddenColumnInExport
                                    isRowSelectionAllowed={false}
                                    disableDownloadByPrintOptions
                                    disableColumnSelector
                                />
                            </Box>
                        )}
                    </>
                ) : (
                    <Box sx={styles.centralTextBox}>
                        <Typography>
                            No Generated Documents for this job
                        </Typography>
                    </Box>
                )}
            </Box>
        </Modal>
    );
};

export default GeneratedDocumentsModal;